import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  input,
  effect,
  untracked,
  viewChild,
} from '@angular/core';
import { getDocument, GlobalWorkerOptions, PDFPageProxy } from 'pdfjs-dist/legacy/build/pdf.mjs';
import {
  calcContainSize,
  canvasPixelSize,
  centerOffset,
  containScale,
  coverScale,
  resolveDevicePixelRatio,
} from './_core/pdf-geometry';

const RESIZE_RENDER_DEBOUNCE_MS = 200;
const RENDERING_CANCELLED_EXCEPTION = 'RenderingCancelledException';

@Component({
  selector: 'fr8bot-ui-pdf-preview',
  templateUrl: './pdf-preview.component.html',
  styleUrl: './pdf-preview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfPreviewUIComponent implements AfterViewInit, OnDestroy {
  readonly wrapperRef = viewChild.required<ElementRef<HTMLElement>>('wrapper');
  readonly bgCanvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('bgCanvas');
  readonly fgCanvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('fgCanvas');

  readonly url = input<string>();
  readonly file = input<File | null>();

  private isViewInit = false;
  private page?: PDFPageProxy;
  private pageAspect?: number;

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;

  private bgRenderTask?: any;
  private fgRenderTask?: any;

  private loadToken = 0;

  constructor() {
    // The first render waits for the canvases in ngAfterViewInit; this only follows later changes.
    effect(() => {
      this.url();
      this.file();

      if (this.isViewInit) {
        untracked(() => this.startRender().then());
      }
    });
  }

  ngAfterViewInit() {
    this.isViewInit = true;

    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleResize();
    });

    this.resizeObserver.observe(this.wrapperRef().nativeElement);

    this.startRender().then();
  }

  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
    try {
      this.bgRenderTask?.cancel?.();
    } catch {}
    try {
      this.fgRenderTask?.cancel?.();
    } catch {}
  }

  private scheduleResize() {
    this.clearCanvases();

    if (this.resizeTimeout) clearTimeout(this.resizeTimeout);

    this.resizeTimeout = window.setTimeout(() => {
      this.render().then();
    }, RESIZE_RENDER_DEBOUNCE_MS);
  }

  private async startRender() {
    const token = ++this.loadToken;

    const source = await this.resolvePdfSource();
    if (!source) return;

    GlobalWorkerOptions.workerSrc = new URL(
      'assets/pdfjs/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();

    const pdf = await getDocument(source).promise;
    if (token !== this.loadToken) return;

    this.page = await pdf.getPage(1);
    if (token !== this.loadToken) return;

    const baseViewport = this.page.getViewport({ scale: 1 });
    this.pageAspect = baseViewport.width / baseViewport.height;

    await this.render();
  }

  private async resolvePdfSource(): Promise<string | { data: Uint8Array } | null> {
    const file = this.file();
    if (file) {
      const buf = await file.arrayBuffer();
      return { data: new Uint8Array(buf) };
    }

    const url = this.url();
    if (url) return url;

    return null;
  }

  private async render() {
    if (!this.page || !this.pageAspect) return;

    const wrapper = this.wrapperRef().nativeElement;
    const bgCanvas = this.bgCanvasRef().nativeElement;
    const fgCanvas = this.fgCanvasRef().nativeElement;

    const wrapperW = wrapper.clientWidth;
    const wrapperH = wrapper.clientHeight;
    if (!wrapperW || !wrapperH) return;

    const { fgCssW, fgCssH } = calcContainSize(wrapperW, wrapperH, this.pageAspect);

    fgCanvas.style.width = `${fgCssW}px`;
    fgCanvas.style.height = `${fgCssH}px`;

    await Promise.all([
      this.renderBackgroundCanvas(this.page, bgCanvas, wrapperW, wrapperH),
      this.renderForegroundCanvas(this.page, fgCanvas, fgCssW, fgCssH),
    ]);
  }

  private async renderBackgroundCanvas(
    page: PDFPageProxy,
    canvas: HTMLCanvasElement,
    cssW: number,
    cssH: number,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (this.bgRenderTask) {
      try {
        await this.bgRenderTask.cancel();
      } catch {}
      this.bgRenderTask = undefined;
    }

    const dpr = resolveDevicePixelRatio(window.devicePixelRatio);
    const baseViewport = page.getViewport({ scale: 1 });

    const pixelSize = canvasPixelSize(cssW, cssH, dpr);
    canvas.width = pixelSize.width;
    canvas.height = pixelSize.height;

    const viewport = page.getViewport({ scale: coverScale(canvas, baseViewport) });
    const offset = centerOffset(canvas, viewport);
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(offset.x, offset.y);

    this.bgRenderTask = page.render({ canvasContext: ctx, viewport } as any);

    try {
      await this.bgRenderTask.promise;
    } catch {}

    ctx.restore();

    ctx.fillStyle = 'rgba(120,120,120,0.35)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    this.bgRenderTask = undefined;
  }

  private async renderForegroundCanvas(
    page: PDFPageProxy,
    canvas: HTMLCanvasElement,
    cssW: number,
    cssH: number,
  ) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (this.fgRenderTask) {
      try {
        await this.fgRenderTask.cancel();
      } catch {}
      this.fgRenderTask = undefined;
    }

    const dpr = resolveDevicePixelRatio(window.devicePixelRatio);
    const baseViewport = page.getViewport({ scale: 1 });

    const pixelSize = canvasPixelSize(cssW, cssH, dpr);
    canvas.width = pixelSize.width;
    canvas.height = pixelSize.height;

    const viewport = page.getViewport({ scale: containScale(canvas, baseViewport) });
    const offset = centerOffset(canvas, viewport);

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    ctx.translate(offset.x, offset.y);

    this.fgRenderTask = page.render({ canvasContext: ctx, viewport } as any);

    try {
      await this.fgRenderTask.promise;
    } catch (e: any) {
      if (e?.name !== RENDERING_CANCELLED_EXCEPTION) throw e;
    }

    ctx.restore();
    this.fgRenderTask = undefined;
  }

  private clearCanvases() {
    const bg = this.bgCanvasRef().nativeElement;
    const fg = this.fgCanvasRef().nativeElement;

    try {
      this.bgRenderTask?.cancel?.();
    } catch {}
    try {
      this.fgRenderTask?.cancel?.();
    } catch {}
    this.bgRenderTask = undefined;
    this.fgRenderTask = undefined;

    bg.getContext('2d')?.clearRect(0, 0, bg.width, bg.height);
    fg.getContext('2d')?.clearRect(0, 0, fg.width, fg.height);
  }
}
